// Enums
export enum Metal {
  AU = 'AU',
  AG = 'AG',
  CU = 'CU',
  PGM = 'PGM',
  ZN = 'ZN',
  PB = 'PB',
}

export enum ProjectStage {
  PRE_DISCOVERY = 'PRE_DISCOVERY',
  BROWNFIELD_EXPLORATION = 'BROWNFIELD_EXPLORATION',
  MAIDEN_MRE = 'MAIDEN_MRE',
  SCOPING_PEA = 'SCOPING_PEA',
  PFS = 'PFS',
  FS = 'FS',
  FID_BUILD = 'FID_BUILD',
  PRODUCTION = 'PRODUCTION',
}

export enum DatePrecision {
  YEAR = 'YEAR',
  SEMESTER = 'SEMESTER',
  QUARTER = 'QUARTER',
  MONTH = 'MONTH',
  DAY = 'DAY',
}

export enum ResourceCategory {
  MEASURED = 'MEASURED',
  INDICATED = 'INDICATED',
  INFERRED = 'INFERRED',
}

export enum ReserveCategory {
  PROVEN = 'PROVEN',
  PROBABLE = 'PROBABLE',
}

export enum UserSource {
  INTERNAL = 'INTERNAL',
  GOOGLE = 'GOOGLE',
}

// Base types
export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  city?: string
  country?: string
  source: UserSource
  favorites: string[]
  watchlists: Watchlist[]
  createdAt: string
  updatedAt: string
}

export interface Watchlist {
  id: string
  name: string
  userId: string
  projectIds: string[]
  createdAt: string
  updatedAt: string
}

export interface Company {
  id: string
  name: string
  projects?: Project[]
  production: ProductionData[]
  costs: CostData[]
  createdAt: string
  updatedAt: string
}

export interface Operator {
  id: string
  name: string
  ownership: number // Percentage of ownership (0-100)
  createdAt: string
  updatedAt: string
}

export interface Owner {
  id: string
  name: string
  ownership: number // Percentage of equity ownership (0-100)
}

export interface Project {
  id: string
  name: string
  operators: Operator[]
  owners: Owner[]
  country: string
  primaryMetal: Metal
  secondaryMetals: Metal[]
  stage: ProjectStage
  firstProductionDate?: string // ISO date format (e.g., "2026-01-01")
  firstProductionPrecision?: DatePrecision // Precision level of the first production date
  resources: ResourceData[]
  reserves: ReserveData[]
  production: ProductionRecord[]
  aisc: CostRecord[]
  cashCost: CostRecord[]
  latitude: number
  longitude: number
  isFavorite: boolean
  createdAt: string
  updatedAt: string
}

export interface ResourceData {
  category: ResourceCategory
  metal: Metal
  tonnage: number // kt (thousands of tonnes)
  grade: number // g/t for Au, Ag, PGM; % for Cu
  content: number // koz for Au, Ag, PGM; kt for Cu
}

export interface ReserveData {
  category: ReserveCategory
  metal: Metal
  tonnage: number // kt (thousands of tonnes)
  grade: number // g/t for Au, Ag, PGM; % for Cu
  content: number // koz for Au, Ag, PGM; kt for Cu
}

export interface ProductionRecord {
  metal: Metal
  production?: number // koz for Au, Ag, PGM; kt for Cu
  min?: number // minimum of guidance range
  max?: number // maximum of guidance range
  period: string
}

export interface CostRecord {
  metal: Metal
  cost?: number // USD per ounce for Au, Ag, PGM; USD per pound for Cu
  min?: number // minimum of guidance range
  max?: number // maximum of guidance range
  period: string
}

export interface ProductionData {
  quarter: string
  year: number
  metal: Metal
  quantity: number
  isEstimated: boolean
}

export interface CostData {
  quarter: string
  year: number
  aisc: number // All-In Sustaining Cost
}

// Input types
export interface ProjectFilter {
  metals?: Metal[]
  stages?: ProjectStage[]
  countries?: string[]
  name?: string
  operator?: string
  mineralResource?: MineralResourceFilterInput
  annualProduction?: AnnualProductionFilterInput
  minResources?: number
  maxResources?: number
  favoritesOnly?: boolean
  watchlistId?: string
}

export interface MineralResourceFilterInput {
  metal: Metal
  minContent?: number
  maxContent?: number
}

export interface AnnualProductionFilterInput {
  metal: Metal
  minProduction?: number
  maxProduction?: number
}

export interface CreateUserInput {
  email: string
  firstName: string
  lastName: string
  city?: string
  country?: string
  source: UserSource
}

export interface UpdateUserFavoritesInput {
  userId: string
  projectId: string
  isFavorite: boolean
}

export interface CreateWatchlistInput {
  userId: string
  name: string
}

export interface UpdateWatchlistInput {
  watchlistId: string
  name?: string
}

export interface AddProjectToWatchlistInput {
  watchlistId: string
  projectId: string
}

export interface RemoveProjectFromWatchlistInput {
  watchlistId: string
  projectId: string
}

// Connection types for pagination
export interface ProjectConnection {
  items: Project[]
  nextToken?: string
  total: number
}

export interface CompanyConnection {
  items: Company[]
  nextToken?: string
  total: number
}

// Auth types
export interface AuthUser {
  id: string
  email: string
  username: string
  firstName: string
  lastName: string
  city?: string
  country?: string
  source: UserSource
  avatarUrl?: string
  authProviders?: string[]
  hasPassword?: boolean
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  email: string
  username: string
  firstName: string
  lastName: string
  city?: string
  country?: string
  turnstileToken?: string
  acceptedPrivacy?: boolean
}

// UI State types
export interface LoadingState {
  isLoading: boolean
  error?: string
}

export interface PaginationState {
  page: number
  limit: number
  total: number
  hasNextPage: boolean
}

// Map types
export interface MapBounds {
  north: number
  south: number
  east: number
  west: number
}

export interface MapMarker {
  id: string
  position: {
    lat: number
    lng: number
  }
  project: Project
}

// Filter state
export interface FilterState {
  metals: Metal[]
  stages: ProjectStage[]
  countries: string[]
  resourceRange: [number, number]
  favoritesOnly: boolean
  watchlistId?: string
  searchTerm: string
}

// Table column definitions
export interface TableColumn {
  id: string
  label: string
  minWidth?: number
  align?: 'left' | 'right' | 'center'
  sortable?: boolean
  format?: (value: any) => string
}